import { useCallback, useEffect, useState } from "react";
import { dismissDissolvedSquad } from "@/lib/dissolvedSquads";
import { fetchMyPendingInvites } from "@/lib/squadInvites";
import { getMyRejoinRequest } from "@/lib/rejoinRequests";
import { getLatestSquadRemoval } from "@/lib/squad";

const KEY = "pulse.leftSquads";
const TTL_MS = 24 * 60 * 60 * 1000;

export type LeftSquadReason = "self_leave" | "kicked";

export type LeftSquad = {
  id: string;
  name: string;
  joinCode?: string;
  leftAt: number;
  reason?: LeftSquadReason;
  pendingRequestId?: string;
  pendingInvite?: boolean;
};

function prune(entries: LeftSquad[]): LeftSquad[] {
  const cutoff = Date.now() - TTL_MS;
  return entries.filter((e) => e.leftAt >= cutoff);
}

function writeLeftSquads(entries: LeftSquad[]) {
  try {
    localStorage.setItem(KEY, JSON.stringify(prune(entries)));
  } catch {}
  window.dispatchEvent(new CustomEvent("pulse:leftSquads"));
}

export function readLeftSquads(): LeftSquad[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LeftSquad[];
    const list = Array.isArray(parsed) ? parsed : [];
    const pruned = prune(list);
    if (pruned.length !== list.length) writeLeftSquads(pruned);
    return pruned;
  } catch {
    return [];
  }
}

export function recordLeftSquad(entry: {
  id: string;
  name: string;
  joinCode?: string;
  leftAt?: number;
  reason?: LeftSquadReason;
  pendingRequestId?: string;
  pendingInvite?: boolean;
}) {
  dismissDissolvedSquad(entry.id);
  const leftAt = entry.leftAt ?? Date.now();
  const rest = readLeftSquads().filter((e) => e.id !== entry.id);
  writeLeftSquads([
    {
      id: entry.id,
      name: entry.name,
      joinCode: entry.joinCode,
      leftAt,
      reason: entry.reason,
      pendingRequestId: entry.pendingRequestId,
      pendingInvite: entry.pendingInvite,
    },
    ...rest,
  ]);
}

export function updateLeftSquad(
  patch: {
    id: string;
    reason?: LeftSquadReason;
    pendingRequestId?: string | null;
    pendingInvite?: boolean | null;
  },
) {
  const list = readLeftSquads();
  const idx = list.findIndex((e) => e.id === patch.id);
  if (idx === -1) return;

  const next = { ...list[idx]! };
  if (patch.reason !== undefined) next.reason = patch.reason;
  if (patch.pendingRequestId !== undefined) {
    if (patch.pendingRequestId === null) delete next.pendingRequestId;
    else next.pendingRequestId = patch.pendingRequestId;
  }
  if (patch.pendingInvite !== undefined) {
    if (patch.pendingInvite) next.pendingInvite = true;
    else delete next.pendingInvite;
  }

  const rest = list.filter((e) => e.id !== patch.id);
  writeLeftSquads([next, ...rest]);
}

export async function hydrateLeftSquads(userId: string): Promise<void> {
  const pendingInvites = await fetchMyPendingInvites();
  const invitedBySquadId = new Map(
    pendingInvites.map((invite) => [invite.squad_id, invite] as const),
  );
  const existingIds = new Set(readLeftSquads().map((ghost) => ghost.id));

  for (const ghost of readLeftSquads()) {
    const invite = invitedBySquadId.get(ghost.id);
    const pendingInvite = invite != null;

    const [request, removal] = await Promise.all([
      getMyRejoinRequest(ghost.id),
      ghost.reason ? Promise.resolve(null) : getLatestSquadRemoval(ghost.id, userId),
    ]);

    const reason: LeftSquadReason | undefined =
      ghost.reason ?? (removal?.reason as LeftSquadReason | undefined);
    const pendingRequestId =
      request?.status === "pending" ? request.id : undefined;

    const unchanged =
      ghost.reason === reason &&
      ghost.pendingRequestId === pendingRequestId &&
      ghost.pendingInvite === pendingInvite;
    if (unchanged) continue;

    updateLeftSquad({
      id: ghost.id,
      reason,
      pendingRequestId: pendingRequestId ?? null,
      pendingInvite: pendingInvite ? true : null,
    });
  }

  for (const invite of pendingInvites) {
    if (existingIds.has(invite.squad_id)) continue;

    recordLeftSquad({
      id: invite.squad_id,
      name: invite.squad_name,
      reason: "kicked",
      pendingInvite: true,
      leftAt: new Date(invite.invited_at).getTime() || Date.now(),
    });
  }
}

export function dismissLeftSquad(squadId: string) {
  writeLeftSquads(readLeftSquads().filter((e) => e.id !== squadId));
}

export function useLeftSquads(userId?: string) {
  const [leftSquads, setLeftSquads] = useState<LeftSquad[]>([]);

  const refresh = useCallback(() => setLeftSquads(readLeftSquads()), []);

  useEffect(() => {
    refresh();
    const h = () => refresh();
    window.addEventListener("pulse:leftSquads", h);
    window.addEventListener("storage", h);
    return () => {
      window.removeEventListener("pulse:leftSquads", h);
      window.removeEventListener("storage", h);
    };
  }, [refresh]);

  useEffect(() => {
    if (!userId) return;
    void hydrateLeftSquads(userId).then(() => refresh());
  }, [userId, refresh]);

  return { leftSquads, dismissLeftSquad, refresh };
}
